import type { Status as TransactionStatus } from "types";
import { ThemePaletteMode } from "./ui";

export const Filters = {
    merchantId: 'merchantId',
    merchantName: 'merchantName',
    methodId: 'methodId',
    settlementStatus: 'settlementStatus',
    fundStatus: 'fundStatus',
    isPaid: 'isPaid',
    status: 'status',
    transactionId: 'transactionId',
    refId: 'refId',
    consumerId: 'consumerId',
    trackingMemberCode: 'trackingMemberCode',
    transactionGuid: 'transactionGuid',
    endToEndId: 'endToEndId',
    currency: 'currency',
    PSP: 'PSP',
    isSettled: 'isSettled',
    email: 'email',
    via: 'via',
    bankId: 'bankId',
    bankName: 'bankName',
    amount: 'amount',
    endToEndIdSlug: 'endToEndIdSlug',
    hashedEmail: 'hashedEmail',
    MOR: 'MOR',
    parentId: 'parentId',
    consumerFirstName: 'consumerFirstName',
    consumerLastName: 'consumerLastName',
    identifier: 'identifier',
    iban: 'iban',
    amountEur: 'amountEur',
    amountEur_total: 'amountEur_total',
    consumerIban: 'consumerIban',
    obResultStatus: 'obResultStatus',
    methodTag: 'methodTag',
    consumerKycTransactionId: 'consumerKycTransactionId',
    cardIssuer: 'cardIssuer',
    clabe: 'clabe',
    bitsoBankName: 'bitsoBankName',
    direction: 'direction',
    refundableAmount: 'refundableAmount',
    isEcommerce: 'isEcommerce'
};

export const OPEN_BANKING_METHOD_TAG = 'op';

export const summs = [
    Filters.amount,
    Filters.amountEur
];

export const text = [
    Filters.consumerId,
    Filters.refId,
    Filters.transactionGuid,
    Filters.transactionId,
    Filters.trackingMemberCode,
    Filters.endToEndId,
    Filters.email,
    Filters.bankId,
    Filters.bankName,
    Filters.amount,
    Filters.endToEndIdSlug,
    Filters.hashedEmail,
    Filters.parentId,
    Filters.consumerFirstName,
    Filters.consumerLastName,
    Filters.consumerIban,
    Filters.consumerKycTransactionId,
    Filters.clabe,
    Filters.refundableAmount
];

export const multi = [
    Filters.settlementStatus,
    Filters.fundStatus,
    Filters.status,
    Filters.currency,
    Filters.PSP,
    Filters.merchantId,
    Filters.methodId,
    Filters.via,
    Filters.bitsoBankName
];

export const single = [
    Filters.isPaid,
    Filters.isSettled,
    Filters.MOR,
    Filters.obResultStatus,
    Filters.cardIssuer,
    Filters.direction,
    Filters.isEcommerce
];

export const enum Psp {
    BankingSdk = 'bankingSdk'
};

export const Banking = {
    filters: [
        Filters.bankName,
        Filters.bankId,
        Filters.endToEndId,
        Filters.endToEndIdSlug
    ],
    visibilityCriterias: [
        35, // methodId (Open Banking)
        50, // (Open Banking TP)
        51, // (Open Banking GC)
        Psp.BankingSdk // psp bankingSdk
    ]
};

export const OpenBankingEmptyResultStatus = {
    BankInfoNotSubmitted: null,
    BankInfoSubmitted: ''
} as const;

export const enum OpenBankingMessageResultStatus {
    PaymentFinalizedWaitingConfirmation = '1',
    RedirectedConsumerToAuthorizePayment = '2',
    RequestedConsumerToAuthorizePayment = '3',
    RequestedPasswordForConsumer = '4',
    RequestedMoreInfoFromConsumer = '5',
    RequestedOptionFromConsumer = '6',
    //  ErrorHasOccurred = '7',
    RetryToFinalizePaymentAfterPeriod = '8',
    NotDefined = '0'
};

export const enum OpenBankingMessageForAccountsResultStatus {
    AccountInitFinalizedWaitingPaymentInit = '1',
    RedirectedConsumerForAccountConfirmation = '2',
    RequestedConsumerToAuthorizeAccount = '3',
    RetryToFinalizeAccountAfterPeriod = '8'
};

export const enum OpenBankingResultStatus {
    BankInfoNotSubmitted = -1,
    BankInfoSubmitted,
    PaymentFinalizedWaitingConfirmation,
    RedirectedConsumerToAuthorizePayment,
    RequestedConsumerToAuthorizePayment,
    RequestedPasswordForConsumer,
    RequestedMoreInfoFromConsumer,
    RequestedOptionFromConsumer,
    //  ErrorHasOccurred,
    RetryToFinalizePaymentAfterPeriod,
    AccountInitFinalizedWaitingPaymentInit,
    RedirectedConsumerForAccountConfirmation,
    RequestedConsumerToAuthorizeAccount,
    RetryToFinalizeAccountAfterPeriod,
    NotDefined
};

export const enum Relations {
    FlowContext = 'flowContext',
    MerchantGroupName = 'merchantGroupName'
}

export const enum Direction {
    C = 'C',
    D = 'D'
};

export const enum RefundStrategy {
    Full = 'Full',
    Partial = 'Partial'
};

export const PER_PAGE_SIZE = 1000;

export const enum Status {
    Rejected = 'REJECT',
    ChargeBack = 'CHARGE_BACK',
    Succeeded = 'SUCCEEDED',
    Failed = 'FAILED',
    Refunded = 'REFUNDED',
    Pending = 'PENDING'
};

export const enum FundStatus {
    NotExpected = 'NOT_EXPECTED',
    Waiting = 'WAITING',
    Received = 'RECEIVED',
    Sent = 'SENT',
    Missing = 'MISSING',
    Refunded = 'REFUNDED',
    PartiallyRefunded = 'PARTIALLY_REFUNDED',
    PartiallySent = 'PARTIALLY_SENT',
    PartiallySentRefunded = 'PARTIALLY_SENT_REFUNDED',
    PartiallyRefundedSent = 'PARTIALLY_REFUNDED_SENT',
    FullySentRefunded = 'FULLY_SENT_REFUNDED',
    FullyRefunded = 'FULLY_REFUNDED',
    FullyRefundedSent = 'FULLY_REFUNDED_SENT',
    FullySent = 'FULLY_SENT',
    Deducted = 'DEDUCTED'
};

export const enum SettlementStatus {
    Succeeded = 'SUCCEEDED',
    FundsMissing = 'FUNDSMISSING',
    FundsReceived = 'FUNDSRECEIVED',
    FundsRefunded = 'FUNDSREFUNDED',
    FundsSent = 'FUNDSSENT'
};

export const statusColorSchemeMap = new Map<TransactionStatus, Record<ThemePaletteMode, string>>()
    .set(Status.Succeeded, {
        [ThemePaletteMode.Dark]: '#36FF56',
        [ThemePaletteMode.Light]: '#24AC4A'
    })
    .set(Status.Rejected, {
        [ThemePaletteMode.Dark]: '#FF9166',
        [ThemePaletteMode.Light]: '#FF8C5A'
    })
    .set(Status.ChargeBack, {
        [ThemePaletteMode.Dark]: '#58AFFF',
        [ThemePaletteMode.Light]: '#5869FF'
    })
    .set(Status.Failed, {
        [ThemePaletteMode.Dark]: '#FF4853',
        [ThemePaletteMode.Light]: '#FF4D57'
    })
    .set(Status.Refunded, {
        [ThemePaletteMode.Dark]: '#DA62FF',
        [ThemePaletteMode.Light]: '#CE50F5'
    })
    .set(Status.Pending, {
        [ThemePaletteMode.Dark]: '#FFEE53',
        [ThemePaletteMode.Light]: '#EFBA00'
    });
